/**
 * Aba de Certificados Digitais A1 — Fase 4.2 eSocial
 * GridProfile / NeuroSynx Ltda
 *
 * Upload do .pfx + senha. Apenas 1 ativo por vez. Exibe titular,
 * CNPJ extraído do SAN ICP-Brasil, emissor, validade e estado.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import {
  FileSignature, Upload, Trash2, CheckCircle2, AlertTriangle,
  Power, ShieldCheck, Calendar, Building2, Loader2,
} from 'lucide-react';
import { useConfirm } from '../../hooks/useConfirm';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt) ? '—' : dt.toLocaleDateString('pt-BR') + ' ' + dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const CertificadosA1Tab = () => {
  const { confirm, ConfirmDialog } = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ arquivo: null, senha: '', descricao: '' });
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/esocial/certificados`, auth());
      setItems(r.data || []);
    } catch (e) { toast.error('Erro ao listar certificados'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submitUpload = async () => {
    if (!form.arquivo) return toast.error('Selecione o arquivo .pfx ou .p12');
    if (!form.senha)   return toast.error('Informe a senha do certificado');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('arquivo', form.arquivo);
      fd.append('senha', form.senha);
      if (form.descricao) fd.append('descricao', form.descricao);
      await axios.post(`${API}/api/esocial/certificados/upload`, fd, {
        ...auth(),
        headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('✅ Certificado carregado e ativado');
      setUploadOpen(false);
      setForm({ arquivo: null, senha: '', descricao: '' });
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao processar certificado');
    } finally { setUploading(false); }
  };

  const ativar = async (id) => {
    try { await axios.post(`${API}/api/esocial/certificados/${id}/ativar`, {}, auth()); toast.success('Ativado'); load(); }
    catch { toast.error('Erro'); }
  };
  const excluir = async (id) => {
    const ok = await confirm({
      title: 'Excluir certificado?',
      description: 'Esta ação é permanente. Se este for o certificado ativo, você precisará cadastrar outro antes de assinar/transmitir.',
      confirmLabel: 'Excluir', destructive: true,
    });
    if (!ok) return;
    try { await axios.delete(`${API}/api/esocial/certificados/${id}`, auth()); toast.success('Excluído'); load(); }
    catch { toast.error('Erro'); }
  };

  return (
    <div className="space-y-4" data-testid="esocial-certs-tab">
      <ConfirmDialog />
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ShieldCheck className="h-5 w-5 text-indigo-600" /> Certificados Digitais A1
            </CardTitle>
            <CardDescription>
              Certificado ICP-Brasil A1 (.pfx ou .p12) usado para assinar e transmitir XMLs eSocial.
              Apenas <b>um</b> pode estar ativo de cada vez.
            </CardDescription>
          </div>
          <Button onClick={() => setUploadOpen(true)} data-testid="cert-upload-btn">
            <Upload className="h-4 w-4 mr-2" /> Carregar Certificado
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileSignature className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>Nenhum certificado cadastrado.</p>
              <p className="text-xs mt-1">Clique em "Carregar Certificado" para fazer o upload do seu A1 (.pfx).</p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((c) => {
                const expirando = c.dias_para_expirar !== null && c.dias_para_expirar >= 0 && c.dias_para_expirar <= 30;
                return (
                  <div key={c.id} className={`border rounded-md p-3 flex items-start gap-3 ${c.ativo ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200'}`}>
                    <div className={`p-2 rounded ${c.ativo ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      <ShieldCheck className={`h-5 w-5 ${c.ativo ? 'text-emerald-600' : 'text-slate-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-800 truncate">{c.titular || '?'}</span>
                        {c.ativo && <Badge className="bg-emerald-600 text-white text-[10px]">ATIVO</Badge>}
                        {c.expirado && <Badge variant="destructive" className="text-[10px]">EXPIRADO</Badge>}
                        {!c.expirado && expirando && <Badge className="bg-amber-500 text-white text-[10px]">EXPIRA EM {c.dias_para_expirar}d</Badge>}
                      </div>
                      <div className="text-xs text-slate-600 mt-1 grid grid-cols-2 gap-x-4">
                        <div><Building2 className="h-3 w-3 inline mr-1" />CNPJ/CPF: <code className="font-mono">{c.titular_cpf_cnpj || '—'}</code></div>
                        <div>Emissor: {c.emissor || '—'}</div>
                        <div><Calendar className="h-3 w-3 inline mr-1" />Válido de: {fmtDate(c.valido_de)}</div>
                        <div><Calendar className="h-3 w-3 inline mr-1" />Válido até: {fmtDate(c.valido_ate)}</div>
                        {c.descricao && <div className="col-span-2 italic text-slate-500">"{c.descricao}"</div>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {!c.ativo && (
                        <Button size="sm" variant="outline" onClick={() => ativar(c.id)} title="Ativar este">
                          <Power className="h-4 w-4 text-emerald-600" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => excluir(c.id)} title="Excluir">
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-amber-50/50 border-amber-200">
        <CardContent className="p-4 flex gap-3 text-sm">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <b>Importante:</b> a senha do certificado é armazenada <b>criptografada</b> com Fernet (AES-128).
            Para transmissão real ao gov.br você deve usar um certificado <b>ICP-Brasil válido</b> emitido pela sua AC (Serasa, AC Safeweb, Certisign, AC Receita etc.) e configurar a variável de ambiente <code className="bg-amber-100 px-1">ESOCIAL_TRANSMISSAO_MODO=real</code>.
            Em modo <code className="bg-amber-100 px-1">mock</code> (padrão) o sistema simula respostas do gov.br para validar o pipeline.
          </div>
        </CardContent>
      </Card>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Carregar Certificado A1</DialogTitle>
            <DialogDescription>
              Faça upload do arquivo .pfx ou .p12 e informe a senha. O sistema extrairá automaticamente o titular, CNPJ e validade.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Arquivo (.pfx ou .p12) *</Label>
              <Input
                ref={fileRef} type="file" accept=".pfx,.p12,application/x-pkcs12"
                onChange={(e) => setForm({ ...form, arquivo: e.target.files?.[0] || null })}
                data-testid="cert-file-input"
              />
            </div>
            <div>
              <Label>Senha do certificado *</Label>
              <Input
                type="password" value={form.senha}
                onChange={(e) => setForm({ ...form, senha: e.target.value })}
                placeholder="••••••••" data-testid="cert-senha-input"
              />
            </div>
            <div>
              <Label>Descrição (opcional)</Label>
              <Input
                value={form.descricao}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                placeholder="Ex: A1 Empresa Matriz - 2026"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancelar</Button>
            <Button onClick={submitUpload} disabled={uploading} data-testid="cert-submit-btn">
              {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processando...</> : <><CheckCircle2 className="h-4 w-4 mr-2" />Carregar e Ativar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CertificadosA1Tab;
